// Sign edit screen (MB.1, UI). A small modal with four text inputs, opened when a
// player interacts with a sign; "Done" writes the (normalized) lines back through
// the supplied callback. Client-only (ENG-1). The 3D text render is a remainder.
import { h } from '../h';
import { SIGN_LINES, SIGN_LINE_MAX, normalizeSignText } from '../../../sim/game/sign';
import { COW } from '../../../sim/config';
import { normalizeNote } from '../../../sim/coworking/text';

/** Open the sign editor over `current`; `onSave` receives the four lines on Done.
 *  Returns a close handle. */
export function openSignEditor(current: readonly string[], onSave: (lines: string[]) => void): () => void {
  const lines = normalizeSignText(current);
  const inputs: HTMLInputElement[] = [];
  for (let i = 0; i < SIGN_LINES; i++) {
    inputs.push(h('input', { class: 'bb-sign-line', type: 'text', maxLength: SIGN_LINE_MAX, value: lines[i] ?? '' }));
  }
  const done = h('button', { class: 'bb-btn bb-sign-done' }, 'Done');
  for (const inp of inputs) inp.style.cssText = 'display:block;margin:4px 0;padding:4px;width:220px;';
  const panel = h('div', { class: 'bb-sign-editor' }, h('div', { class: 'bb-sign-title' }, 'Edit Sign'), ...inputs, done);
  panel.style.cssText = 'background:#2b2b2b;color:#fff;padding:16px;border:2px solid #000;text-align:center;';
  const overlay = h('div', { class: 'bb-modal bb-sign-modal' }, panel);
  // Explicit inline stacking so the modal is reliably on top + clickable.
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:#0006;';
  document.body.append(overlay);
  const close = (): void => { overlay.remove(); };
  done.addEventListener('click', () => { onSave(inputs.map((i) => i.value)); close(); });
  inputs[0]?.focus();
  return close;
}

/** Open the sticky-note editor over `current` (M55.4, ARCH §30.3, COW-2) — the
 *  same single-modal shape as `openSignEditor`, one line instead of four, capped
 *  at `COW.NOTE_MAX`. */
export function openNoteEditor(current: string, onSave: (text: string) => void): () => void {
  const input = h('input', { class: 'bb-note-line', type: 'text', maxLength: COW.NOTE_MAX, value: normalizeNote(current) });
  input.style.cssText = 'display:block;margin:4px 0;padding:4px;width:220px;';
  const done = h('button', { class: 'bb-btn bb-note-done' }, 'Done');
  const panel = h('div', { class: 'bb-note-editor' }, h('div', { class: 'bb-note-title' }, 'Sticky Note'), input, done);
  panel.style.cssText = 'background:#2b2b2b;color:#fff;padding:16px;border:2px solid #000;text-align:center;';
  const overlay = h('div', { class: 'bb-modal bb-note-modal' }, panel);
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:#0006;';
  document.body.append(overlay);
  const close = (): void => { overlay.remove(); };
  done.addEventListener('click', () => { onSave(input.value); close(); });
  input.focus();
  return close;
}
